import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../../lib/api";
import { violationMaintenanceApi, type ViolationDeduplicationSettings } from "../../lib/api/network/violationMaintenance";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";
import { Alert } from "../ui/Alert";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { DescriptionList } from "../ui/DescriptionList";
import { Switch } from "../ui/Switch";

const queryKey = ["violation-maintenance"] as const;

function message(error: unknown): string {
  if (error instanceof ApiError) return t(`errors.${error.code}`, error.message);
  return error instanceof Error ? error.message : t("errors.network");
}

export function ViolationMaintenancePanel() {
  useLocale();
  const qc = useQueryClient();
  const [draftEnabled, setDraftEnabled] = useState<boolean | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const query = useQuery({
    queryKey,
    queryFn: violationMaintenanceApi.get,
    retry: false,
    refetchOnWindowFocus: "always",
    refetchInterval: (state) => state.state.data && !state.state.error ? 30_000 : false,
  });
  const unsupported = query.error instanceof ApiError && [404, 405, 501].includes(query.error.status);
  const supported = typeof query.data?.enabled === "boolean" && typeof query.data?.interval_hours === "number";
  const enabled = draftEnabled ?? query.data?.enabled ?? false;
  const busy = query.data?.last_status === "queued" || query.data?.last_status === "running";

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      const result = await violationMaintenanceApi.update(next);
      if (result.enabled !== next || result.interval_hours !== 48) throw new Error(t("operations.maintenance.notSaved"));
      return result;
    },
    onSuccess: (data) => { qc.setQueryData(queryKey, data); setDraftEnabled(null); },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });
  const run = useMutation({
    mutationFn: violationMaintenanceApi.run,
    onSuccess: (data) => {
      setConfirmRun(false);
      qc.setQueryData<ViolationDeduplicationSettings>(queryKey, (previous) => previous
        ? { ...previous, last_status: data.status } : previous);
      // Refresh both aggregate pivots and raw-event queries; do not assume a queued job has completed.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey.some((part) => typeof part === "string" && /violation/.test(part)) });
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });
  const canChange = supported && !query.isError && !save.isPending && !run.isPending;
  return (
    <Card title={t("operations.maintenance.title")} description={t("operations.maintenance.description")}>
      <div className="stack">
        <Alert tone="info">{t("operations.maintenance.scope")}</Alert>
        {unsupported && <Alert tone="warning">{t("operations.maintenance.unsupported")}</Alert>}
        {query.error && !unsupported && <Alert tone="danger">{message(query.error)}</Alert>}
        {!query.isPending && !query.error && !supported && <Alert tone="warning">{t("operations.maintenance.unsupported")}</Alert>}
        <Switch checked={enabled} onChange={setDraftEnabled} disabled={!canChange}
          label={t("operations.maintenance.automatic")} sub={t("operations.maintenance.cadence")} />
        {supported && query.data?.interval_hours !== 48 && <Alert tone="warning">{t("operations.maintenance.standardRequired")}</Alert>}
        <DescriptionList items={[
          { labelKey: "operations.maintenance.interval", value: t("operations.maintenance.every48Hours") },
          { labelKey: "operations.maintenance.lastRun", value: formatDateTime(query.data?.last_run_at) },
          { labelKey: "operations.maintenance.nextRun", value: formatDateTime(query.data?.next_run_at) },
          { labelKey: "operations.maintenance.status", value: <Badge tone={query.data?.last_status === "failed" ? "danger" : "neutral"}>{t(`operations.maintenance.status.${query.data?.last_status ?? "unknown"}`)}</Badge> },
          { labelKey: "operations.maintenance.merged", value: query.data?.last_merged_rows ?? "-" },
        ]} />
        {(save.error || run.error) && <Alert tone="danger">{message(save.error || run.error)}</Alert>}
        <div className="row">
          <Button type="button" disabled={!canChange || (draftEnabled === null && query.data?.interval_hours === 48)} loading={save.isPending} onClick={() => save.mutate(enabled)}>{t("common.save")}</Button>
          <Button type="button" variant="secondary" disabled={!canChange || busy || query.data?.interval_hours !== 48} loading={run.isPending} onClick={() => { run.reset(); setConfirmRun(true); }}>{t("operations.maintenance.runNow")}</Button>
          <Button type="button" variant="ghost" disabled={query.isFetching} onClick={() => void query.refetch()}>{t("operations.associations.refresh")}</Button>
        </div>
      </div>
      <ConfirmDialog open={confirmRun} title={t("operations.maintenance.runNow")}
        body={t("operations.maintenance.confirm")} loading={run.isPending} tone="primary"
        onCancel={() => { if (!run.isPending) setConfirmRun(false); }}
        onConfirm={() => { if (canChange && !busy) run.mutate(); }} />
    </Card>
  );
}
